import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddImage_Icon from '../../images/drivers/AddImage.svg';
import DeleteIcon from '@mui/icons-material/Delete';

export default function PhotoUploadBox({
  selectedImage,
  onImageUpload,
  onRemoveImage,
  error,
  isUploaded,
  preview,
}) {
  const [localPreview, setLocalPreview] = useState(preview);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    console.log('DriverPhotoUploadBox: Setting preview to', preview);
    if (preview) {
      setLocalPreview(preview);
      setLoadError(null);
    }
  }, [preview]);

  useEffect(() => {
    if (selectedImage?.location && selectedImage.isExisting) {
      setLocalPreview(selectedImage.location);
      setLoadError(null);
      console.log('DriverPhotoUploadBox: Updated localPreview from selectedImage:', selectedImage.location);
    }
  }, [selectedImage]);

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      if (file.size > 10 * 1024 * 1024) {
        onImageUpload(null, 'File size must be under 10MB');
        setLoadError('File size must be under 10MB');
        return;
      }
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        onImageUpload(null, 'Only PNG, JPEG, or GIF allowed');
        setLoadError('Only PNG, JPEG, or GIF allowed');
        return;
      }
      onImageUpload(file, '');
      setLoadError(null);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLocalPreview(e.target.result);
        console.log('DriverPhotoUploadBox: New file selected, localPreview:', e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemove = () => {
    if (localPreview && !selectedImage?.isExisting) {
      URL.revokeObjectURL(localPreview);
    }
    setLocalPreview(null);
    setLoadError(null);
    onRemoveImage();
    console.log('DriverPhotoUploadBox: Image removed');
  };

  const handleImageError = () => {
    console.error('DriverPhotoUploadBox: Image load failed for URL:', localPreview);
    setLoadError('Unable to load driver image');
    // Keep localPreview to allow retry
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          border: error || loadError ? '2px dashed #D32F2F' : '2px dashed #000',
          borderRadius: '50%',
          backgroundColor: '#E8F0FE',
          width: 200,
          height: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          overflow: 'hidden',
        }}
      >
        {localPreview && !loadError ? (
          <img
            src={localPreview}
            alt="Driver Profile"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={handleImageError}
          />
        ) : (
          <img src={AddImage_Icon} alt="Driver Icon" />
        )}
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            cursor: 'pointer',
          }}
          id="driver-photo-input"
        />
      </Box>
      {localPreview && (
        <>
          <IconButton
            sx={{
              position: 'absolute',
              top: 0,
              right: 20,
              backgroundColor: '#000',
              color: '#FFF',
              '&:hover': { backgroundColor: '#333' },
              width: 40,
              height: 40,
            }}
            component="label"
            htmlFor="driver-photo-input"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            sx={{
              position: 'absolute',
              top: 0,
              right: -20,
              backgroundColor: '#D32F2F',
              color: '#FFF',
              '&:hover': { backgroundColor: '#B71C1C' },
              width: 40,
              height: 40,
            }}
            onClick={handleRemove}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      )}
      {isUploaded && localPreview && !loadError && (
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 10,
            left: 0,
            right: 0,
            textAlign: 'center',
            backgroundColor: 'rgba(255,255,255,0.7)',
            padding: '4px 8px',
            color: 'green',
            borderRadius: '4px',
          }}
        >
          ✓ Uploaded
        </Typography>
      )}
      {(error || loadError) && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 1, textAlign: 'center', display: 'block' }}
        >
          {error || loadError}
        </Typography>
      )}
    </Box>
  );
}